import type { OverviewKpiPoint, OverviewKpiSeries, KpiCatalogEntry } from "@nexus/contracts";
import { computePctChange } from "@/features/dashboard/lib/format";
import { clampKpiDecimals } from "@/features/kpi-settings/lib/kpi-decimals";

/**
 * Curated fallback KPI set for Topic Detail when the catalog has not loaded.
 * Production rendering uses visible catalog rows in `index` order.
 */
export const TOPIC_DETAIL_KPIS = [
  "Spend", "Thumbstop", "Holdrate", "CTR",
  "Conversions", "ROAS", "CPA", "AOV",
  "Comments", "Likes", "Stick Rate", "Spend/Creatives",
  "Spend %", "CPC", "CR", "CPM",
  "Spend/Sticked", "Engagement", "Ads", "Creatives",
  "Frequency", "Post Engagements",
] as const;

export type TopicDetailKpi = (typeof TOPIC_DETAIL_KPIS)[number];

/**
 * Fallback "better direction" for drawer KPIs when `Kpi.polarity` is absent.
 * Production uses `polarity`: POSITIVE = low-is-good, NEGATIVE = high-is-good.
 */
export const KPI_BETTER_DIRECTION: Record<TopicDetailKpi, "high" | "low" | "neutral"> = {
  Spend: "high",
  Thumbstop: "high",
  Holdrate: "high",
  CTR: "high",
  Conversions: "high",
  ROAS: "high",
  CPA: "low",
  AOV: "high",
  Comments: "high",
  Likes: "high",
  "Stick Rate": "high",
  "Spend/Creatives": "low",
  "Spend %": "neutral",
  CPC: "low",
  CR: "high",
  CPM: "low",
  "Spend/Sticked": "low",
  Engagement: "high",
  Ads: "neutral",
  Creatives: "neutral",
  Frequency: "low",
  "Post Engagements": "high",
};

export const SHARE_OF_TOTAL_KPIS = new Set<string>(["Spend", "Value", "Ads", "Creatives"]);

export const ZERO_BADGE_KPIS = new Set<string>(["Frequency"]);

export type KpiTone = "green" | "red" | "neutral";

export const KPI_TONE_CLASS: Record<KpiTone, string> = {
  green: "text-green-600 dark:text-green-400",
  red: "text-red-600 dark:text-red-400",
  neutral: "text-muted-foreground",
};

export interface KpiBadge {
  mainValue: string;
  badgeText: string | null;
  tone: KpiTone;
}

export function visibleTopicDetailKpis(catalog: KpiCatalogEntry[]): KpiCatalogEntry[] {
  return catalog.filter((row) => row.visible).sort((a, b) => a.index - b.index);
}

function formatPct(value: number): string {
  return `${value.toFixed(2)}%`;
}

function roundToDisplayPrecision(value: number, format: OverviewKpiSeries["format"], decimals = 2): number {
  const safeDecimals = clampKpiDecimals(decimals);
  if (format === "PERCENT") return Number((value * 100).toFixed(safeDecimals)) / 100;
  return Number(value.toFixed(safeDecimals));
}

function directionFromPolarity(
  polarity: OverviewKpiSeries["polarity"] | undefined,
  kpi: string,
): "high" | "low" | "neutral" {
  if (polarity === "POSITIVE") return "low";
  if (polarity === "NEGATIVE") return "high";
  return KPI_BETTER_DIRECTION[kpi as TopicDetailKpi] ?? "neutral";
}

export function getKpiTone(
  kpi: string,
  pct: number | null,
  polarity?: OverviewKpiSeries["polarity"],
): KpiTone {
  const direction = directionFromPolarity(polarity, kpi);
  if (direction === "neutral" || pct === null || Number.isNaN(pct) || pct === 0) {
    return "neutral";
  }
  if (direction === "high") return pct < 0 ? "red" : "green";
  return pct < 0 ? "green" : "red";
}

export function resolveKpiBadge(
  kpi: string,
  point: Pick<OverviewKpiPoint, "value" | "formatted">,
  series: Pick<OverviewKpiSeries, "avg" | "avgFormatted" | "format" | "hasSumFormula" | "polarity">,
  decimals = 2,
): KpiBadge {
  const noBadge: KpiBadge = { mainValue: point.formatted, badgeText: null, tone: "neutral" };

  if (series.hasSumFormula) return noBadge;
  if (point.value == null) return noBadge;
  if (ZERO_BADGE_KPIS.has(kpi)) return { mainValue: point.formatted, badgeText: formatPct(0), tone: "neutral" };
  if (series.avg == null || series.avg === 0) return noBadge;

  if (SHARE_OF_TOTAL_KPIS.has(kpi)) {
    const share = (point.value / series.avg) * 100;
    const mainValue = series.avgFormatted
      ? `${point.formatted}/${series.avgFormatted}`
      : point.formatted;
    return { mainValue, badgeText: formatPct(share), tone: getKpiTone(kpi, share, series.polarity) };
  }

  const deviation = computePctChange(roundToDisplayPrecision(point.value, series.format, decimals), series.avg);
  if (Number.isNaN(deviation)) return noBadge;
  return {
    mainValue: point.formatted,
    badgeText: formatPct(deviation),
    tone: getKpiTone(kpi, deviation, series.polarity),
  };
}
